import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import {
  AppPageComponent,
  AppBarComponent,
  AppBarCloseAction,
  AppBarActionsComponent,
  SyncGuardHelper,
  UserMessageGuard,
} from 'common';
import { AccountTransferDrawComponent } from '../account-transfer/account-transfer-draw/account-transfer-draw.component';
import { AccountSetupBeginComponent } from './account-setup-begin/account-setup-begin.component';
import { AccountTransferDrawResolver } from '../account-transfer/account-transfer-draw.resolver';
import { LoanResolver, LoanSigningInfoResolver } from '../../loan/infrastructure/loan.resolver';
import { SsoAccountCurrentResolver } from 'common';
import { AppBarTitleComponent } from '../../app-bar-title/app-bar-title.component';
import { LoanOpenGuard } from '../../loan/infrastructure/loan.guard';
import { OfferBundlesInfoResolver } from '../../renewal/renewal.resolver';
import { OAuthGuard } from '../../../guards/oauth.guard';
import { AppBarContactComponent } from '../../app-bar-contact/app-bar-contact.component';
import { AgreementGuard } from '../../renewal-eligible/renewal-confirmation/sign-agreement-needed.guard';
import { SetupBeginComponent } from './setup-begin/setup-begin.component';
import { LocAgreementFormComponent } from '../../loc-agreement/loc-agreement-form/loc-agreement-form.component';
import { DocuSignReturnComponent } from '../../loc-agreement/docusign-return/docusign-return.component';
import { AccountSetupGuard } from './account-setup.guard';
import { VerifyIdentityComponent } from './verify-identity/verify-identity.component';
import { VerifyIdentityGuard } from './verify-identity/verify-identity.guard';
import { CheckoutStateGuard } from '../../agreement/checkout-state.guard';
import { VerifyIdentityResultComponent } from './verify-identity-result/verify-identity-result.component';
import { IdVerseCompletedCallbackComponent } from './id-verse-completed-callback/id-verse-completed-callback.component';
import { IdVerseCompletedCallbackGuard } from './id-verse-completed-callback/id-verse-completed-callback.guard';

const routes: Routes = [
  {
    path: 'idverse-completed',
    canActivate: [SyncGuardHelper],
    data: {
      syncGuards: [
        OAuthGuard,
        IdVerseCompletedCallbackGuard,
      ]
    },
    component: IdVerseCompletedCallbackComponent,
  },
  {
    path: 'account/:accountId/setup',
    pathMatch: 'full',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appBar: {
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true
      },
      appBarTitle: {
        content: 'Account setup'
      }
    },
    resolve: {
      currentAccount: SsoAccountCurrentResolver,
      loan: LoanSigningInfoResolver
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent,
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        canActivate: [AccountSetupGuard],
        component: AccountSetupBeginComponent
      }
    ]
  },
  {
    path: 'account/setup',
    pathMatch: 'full',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appBar: {
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true
      },
      appBarTitle: {
        content: 'What happens next'
      }
    },
    resolve: {
      currentAccount: SsoAccountCurrentResolver
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent,
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        canActivate: [AccountSetupGuard],
        component: SetupBeginComponent
      }
    ]
  },
  {
    path: 'account/setup/verify-identity',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appBar: {
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true,
      },
      appBarTitle: {
        content: 'Account setup'
      }
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        component: VerifyIdentityComponent,
        canActivate: [SyncGuardHelper],
        data: {
          syncGuards: [
            AccountSetupGuard,
            VerifyIdentityGuard,
          ]
        },
      }
    ]
  },
  {
    path: 'account/setup/verify-identity-result',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appBar: {
        closeAction: AppBarCloseAction.SIGN_OUT,
        expandable: false,
        logoHidden: true,
      },
      appBarTitle: {
        content: 'Account setup'
      }
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        component: VerifyIdentityResultComponent
      }
    ]
  },
  {
    path: 'account/:accountId/setup/sign-agreement',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appPage: {
        contentAlignment: 'stretch-stretch',
        contentWithoutPadding: true,
      },
      appBar: {
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true,
      },
      appBarTitle: {
        content: 'Sign agreement'
      }
    },
    resolve: {
      currentSsoAccount: SsoAccountCurrentResolver,
      loan: LoanSigningInfoResolver,
      offerBundlesInfo: OfferBundlesInfoResolver
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        canActivate: [SyncGuardHelper],
        data: {
          syncGuards: [
            CheckoutStateGuard,
            AgreementGuard
          ]
        },
        component: LocAgreementFormComponent
      }
    ]
  },
  {
    path: 'account/:accountId/setup/sign-agreement/return',
    component: AppPageComponent,
    canActivate: [OAuthGuard],
    data: {
      appPage: {
        contentAlignment: 'stretch-stretch',
        contentWithoutPadding: true,
      },
      appBar: {
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true,
      },
      appBarTitle: {
        content: 'Sign agreement'
      }
    },
    resolve: {
      currentSsoAccount: SsoAccountCurrentResolver,
      loan: LoanSigningInfoResolver,
      offerBundlesInfo: OfferBundlesInfoResolver
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarContactComponent
          }
        ]
      },
      {
        path: '',
        canActivate: [SyncGuardHelper],
        data: {
          syncGuards: [
            CheckoutStateGuard
          ]
        },
        component: DocuSignReturnComponent
      }
    ]
  },
  {
    path: 'account/:accountId/setup/make-a-draw',
    component: AppPageComponent,
    canActivate: [OAuthGuard, UserMessageGuard],
    data: {
      appBar: {
        stickyDisabled: true,
        closeAction: AppBarCloseAction.HOME,
        expandable: false,
        logoHidden: true,
      },
      appBarActions: {
        menuMobile: true,
      },
      appPage: {
        contentClass: "simple"
      }
    },
    children: [
      {
        path: '',
        outlet: 'app-bar',
        component: AppBarComponent,
        children: [
          {
            path: '',
            outlet: 'title',
            component: AppBarTitleComponent,
          },
          {
            path: '',
            outlet: 'right',
            component: AppBarActionsComponent,
          }
        ]
      },
      {
        path: '',
        component: AccountTransferDrawComponent,
        canActivate: [LoanOpenGuard],
        data: { redirectedFromAgreement: true },
        resolve: {
          accountTransfer: AccountTransferDrawResolver,
          loan: LoanResolver
        }
      }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class AccountSetupRoutingModule { }
